// @flow

// PinVideoEndCard is an overlay that can appear when a video finishes playing. The overlay provides two
// affordances: a watch again button and a visit site button. The watch again button will loop the video, and on
// completion show the end frame again. The visit site button will take the user to the website behind the pin.
// The end card exclusively appears on a subset of promoted video pins. There are some video pins which advertisers
// prefer we continually loop video (awareness advertisers) and there are other advertisers which are interested in
// performance/traffic and for those we show the end card.
//
// This component works on LEGO pin rep. For the non-LEGO pin rep this logic needs to be reimplemented in the PinActionWrapper.

import React from 'react';
import i18n from 'app/common/lib/i18n';
import { type Pin } from 'app/common/react/components/PinRep/types';
import { Box, Icon, Image, Text, Touchable } from 'gestalt';

const WATCH_AGAIN_ICON_PATH = {
  __path:
    'M12 18.5A6.508 6.508 0 0 1 5.5 12c0-1.706.689-3.307 1.835-4.5H6.5a1 1 0 1 1 0-2h4.185v4.474a1 1 0 0 1-2 0V8.972A4.505 4.505 0 0 0 7.5 12c0 2.481 2.019 4.5 4.5 4.5s4.5-2.019 4.5-4.5a4.49 4.49 0 0 0-3.112-4.283 1 1 0 1 1 .615-1.902A6.482 6.482 0 0 1 18.5 12c0 3.584-2.916 6.5-6.5 6.5M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0',
};

const VISIT_SITE_ICON_PATH = {
  __path:
    'M17.496 14.572a1 1 0 0 1-1 1h-.001a1.001 1.001 0 0 1-.999-1.001l.002-4.652-7.29 7.288a.995.995 0 0 1-.707.293.999.999 0 0 1-.707-1.707l7.293-7.29L9.428 8.5a1 1 0 0 1 .001-2h.001l8.071.005-.005 8.067zM12 0C5.373 0 0 5.373 0 12c0 6.628 5.373 12 12 12 6.628 0 12-5.372 12-12 0-6.627-5.372-12-12-12z',
};

const Styles = {
  overlay: {
    __style: {
      backgroundColor: '#000',
      opacity: 0.8,
    },
  },
};

type Props = {|
  backgroundImage: string,
  naturalHeight: number,
  naturalWidth: number,
  onRestartVideo: ({
    event: SyntheticMouseEvent<HTMLDivElement> | SyntheticKeyboardEvent<HTMLDivElement>,
  }) => void,
  pin: Pin,
  pinTitle?: string,
|};

export default function PinVideoEndCard({
  backgroundImage,
  naturalHeight,
  naturalWidth,
  onRestartVideo,
  pinTitle,
  pin,
}: Props) {
  const { id } = pin;

  return (
    <Image
      alt={pinTitle || i18n._('pin image', 'image of a pin')}
      naturalHeight={naturalHeight}
      naturalWidth={naturalWidth}
      src={backgroundImage}
    >
      <Box position="absolute" top bottom left right dangerouslySetInlineStyle={Styles.overlay} />
      <Box
        justifyContent="center"
        direction="column"
        display="flex"
        position="absolute"
        height="100%"
        width="100%"
        data-test-id={`video-end-card-${id}`}
      >
        {/* Note: the visit site touchable does not need to handle the click. We can let the click pass through
            and let the default promoted pin clickthrough behavior handle it */}
        <Touchable shape="rounded">
          <Box display="flex" justifyContent="center" padding={2}>
            <Icon
              accessibilityLabel={i18n._('Visit site', "Button when pressed goes to pin's website")}
              color="white"
              dangerouslySetSvgPath={VISIT_SITE_ICON_PATH}
              size={25}
            />
            <Box margin={1} />
            <Text color="white" size="lg" truncate weight="bold">
              {i18n._('Visit site', "Button when pressed goes to pin's website")}
            </Text>
          </Box>
        </Touchable>
        <Box margin={3} />
        <Touchable shape="rounded" onTouch={onRestartVideo}>
          <Box display="flex" justifyContent="center" padding={2}>
            <Icon
              accessibilityLabel={i18n._('Watch again', 'Button when pressed restarts the video')}
              color="white"
              dangerouslySetSvgPath={WATCH_AGAIN_ICON_PATH}
              size={25}
            />
            <Box margin={1} />
            <Text color="white" size="lg" truncate weight="bold">
              {i18n._('Watch again', 'Button when pressed restarts the video')}
            </Text>
          </Box>
        </Touchable>
      </Box>
    </Image>
  );
}
